import { useCallback, useState } from 'react';
import storeLocationsService from '../services/storeLocationsService';
import type { StoreLocationType } from '../../../../types/features/storeDetails/locations';
import {
  DEFAULT_VENDING_BIN_COUNT,
  parseVendingBinCount,
  VENDING_BIN_COUNT_ERROR,
} from '../utils/vendingBinCount';

type StoreLocationForEdit = {
  id: string;
  location_key: string;
  location_name: string;
  location_type: StoreLocationType;
  vending_bin_count?: number | null;
};

type UseUpdateLocationDialogOptions = {
  storeId: string;
  onUpdated?: () => void | Promise<void>;
  onRegistersRefreshed?: () => void | Promise<void>;
};

export const useUpdateLocationDialog = ({
  storeId,
  onUpdated,
  onRegistersRefreshed,
}: UseUpdateLocationDialogOptions) => {
  const [open, setOpen] = useState(false);
  const [location, setLocation] = useState<StoreLocationForEdit | null>(null);
  const [locationName, setLocationName] = useState('');
  const [vendingBinCount, setVendingBinCount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleOpen = useCallback((nextLocation: StoreLocationForEdit) => {
    setLocation(nextLocation);
    setLocationName(nextLocation.location_name);
    setVendingBinCount(nextLocation.location_type === 'vending'
      ? String(nextLocation.vending_bin_count ?? DEFAULT_VENDING_BIN_COUNT)
      : '');
    setError('');
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }

    setOpen(false);
    setLocation(null);
    setLocationName('');
    setVendingBinCount('');
    setError('');
  }, [submitting]);

  const handleSubmit = useCallback(async () => {
    if (!storeId || !location) {
      setError('Location is not available.');
      return;
    }

    if (!locationName.trim()) {
      setError('Please enter a location name.');
      return;
    }

    const parsedVendingBinCount = parseVendingBinCount(vendingBinCount);
    if (location.location_type === 'vending' && parsedVendingBinCount === null) {
      setError(VENDING_BIN_COUNT_ERROR);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await storeLocationsService.updateStoreLocation(storeId, location.id, {
        location_name: locationName.trim(),
        ...(location.location_type === 'vending' && parsedVendingBinCount !== null
          ? { vending_bin_count: parsedVendingBinCount }
          : {}),
      });
      setOpen(false);
      setLocation(null);
      setLocationName('');
      setVendingBinCount('');
      onUpdated?.();
      await onRegistersRefreshed?.();
    } catch (updateError) {
      const message = updateError instanceof Error ? updateError.message : 'Failed to update location.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }, [location, locationName, onRegistersRefreshed, onUpdated, storeId, vendingBinCount]);

  return {
    error,
    handleClose,
    handleOpen,
    handleSubmit,
    locationKey: location?.location_key ?? '',
    locationName,
    locationType: (location?.location_type ?? '') as StoreLocationType | '',
    open,
    submitting,
    setLocationName,
    setVendingBinCount,
    vendingBinCount,
  };
};
